import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useAuthState } from "react-firebase-hooks/auth";
import auth from "../../../firebase.init";
import { Link } from "react-router-dom";
import { MessageSquare, ChevronRight, Plus } from "lucide-react";

const BuyerSupport = () => {
  const [user] = useAuthState(auth);
  const [tickets, setTickets] = useState([]);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Filter states
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (user?.email) {
      fetchTickets();
    }
  }, [user]);

  useEffect(() => {
    applyFilters();
  }, [tickets, statusFilter, priorityFilter, dateRange]);

  useEffect(() => {
    scrollToBottom();
  }, [selectedTicket]);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(
        "https://allinone-clone-7d5d795e98ce.herokuapp.com/support-messages",
        {
          params: { userEmail: user?.email },
        }
      );
      setTickets(response.data);
      setFilteredTickets(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...tickets];

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (ticket) =>
          ticket.ticketStatus.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    // Priority filter
    if (priorityFilter !== "all") {
      filtered = filtered.filter(
        (ticket) =>
          ticket.priority.toLowerCase() === priorityFilter.toLowerCase()
      );
    }

    // Date range filter
    if (dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      filtered = filtered.filter((ticket) => {
        const ticketDate = new Date(ticket.createdAt);
        return ticketDate >= start && ticketDate <= end;
      });
    }

    setFilteredTickets(filtered);
  };

  const handleTicketClick = async (ticketId) => {
    try {
      const response = await axios.get(
        `https://allinone-clone-7d5d795e98ce.herokuapp.com/support-message/${ticketId}`
      );
      setSelectedTicket(response.data);
    } catch (error) {
      console.error("Error fetching ticket details:", error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSendingMessage(true); // start loading

    try {
      await axios.put(
        `https://allinone-clone-7d5d795e98ce.herokuapp.com/support-message-status/${selectedTicket._id}`,
        {
          ticketStatus: "in-progress",
          adminMessage: [
            ...(selectedTicket.adminMessage || []),
            {
              message: newMessage,
              timestamp: new Date(),
              sender: "user",
            },
          ],
        }
      );

      await handleTicketClick(selectedTicket._id); // refresh ticket
      setNewMessage("");
      setTimeout(scrollToBottom, 100);
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setSendingMessage(false); // stop loading
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "in-progress":
        return "bg-warning";
      case "resolved":
        return "bg-success";
      case "closed":
        return "bg-secondary";
      default:
        return "bg-info";
    }
  };

  const getAllMessages = (ticket) => {
    if (!ticket) return [];

    const initialMessage = {
      message: ticket.message,
      timestamp: ticket.createdAt,
      sender: "user",
    };

    return [initialMessage, ...(ticket.adminMessage || [])];
  };

  if (loading) {
    return <div className="loading-spinner">Loading...</div>;
  }

  return (
    <div className="container-fluid p-4">
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
        <div>
          <h3 className="fw-bold mb-1 d-flex align-items-center gap-2">
            <MessageSquare size={24} />
            Support Messages
          </h3>
          <div className="d-flex align-items-center gap-2 small text-white-50">
            <span>User</span>
            <ChevronRight size={14} />
            <span className="text-primary">Messages</span>
          </div>
        </div>
        <Link to="/user/create-support" className="btn btn-primary d-flex align-items-center gap-2" style={{ borderRadius: '20px' }}>
          <Plus size={16} />
          Create Ticket
        </Link>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-header bg-transparent py-3 border-bottom">
          <div className="row g-3">
            <div className="col-md-4">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Status</option>
                <option value="open">Open</option>
                <option value="in-progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="col-md-4">
              <input
                type="date"
                className="form-control"
                value={dateRange.startDate}
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    startDate: e.target.value,
                  }))
                }
              />
            </div>
            <div className="col-md-4">
              <input
                type="date"
                className="form-control"
                value={dateRange.endDate}
                onChange={(e) =>
                  setDateRange((prev) => ({
                    ...prev,
                    endDate: e.target.value,
                  }))
                }
              />
            </div>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="row g-0">
            {/* Tickets Sidebar */}
            <div className="col-lg-4 border-end" style={{ maxHeight: '80vh' }}>
              <div className="p-3 border-bottom">
                <h6 className="mb-0 fw-bold">Support Tickets ({filteredTickets.length})</h6>
              </div>
              <div className="overflow-auto" style={{ height: 'calc(80vh - 57px - 89px)' }}>
                {filteredTickets.map((ticket) => (
                  <div
                    key={ticket._id}
                    onClick={() => handleTicketClick(ticket._id)}
                    className={`border-bottom transition-all p-3 ${
                      selectedTicket?._id === ticket._id ? "bg-secondary bg-opacity-25" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div>
                        <h6 className="mb-1 text-white">{ticket.subject}</h6>
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span
                            className={`badge ${getStatusBadgeClass(
                              ticket.ticketStatus
                            )}`}
                          >
                            {ticket.ticketStatus}
                          </span>
                        </div>
                      </div>
                      <small className="text-white-50">
                        {new Date(ticket.createdAt).toLocaleDateString()}
                      </small>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Chat Area */}
            <div className="col-lg-8 d-flex flex-column" style={{ maxHeight: '80vh' }}>
              {selectedTicket ? (
                <>
                  {/* Chat Header */}
                  <div className="p-3 border-bottom">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h6 className="mb-0 fw-bold">{selectedTicket.subject}</h6>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span
                        className={`badge ${getStatusBadgeClass(
                          selectedTicket.ticketStatus
                        )}`}
                      >
                        {selectedTicket.ticketStatus}
                      </span>
                    </div>
                  </div>

                  {/* Messages Area */}
                  <div className="flex-grow-1 overflow-auto p-4">
                    <div className="d-flex flex-column gap-3">
                      {getAllMessages(selectedTicket).map((msg, index) => (
                        <div
                          key={index}
                          className={`d-flex flex-column mb-3 ${
                            msg.sender === "user" ? "align-items-end" : "align-items-start"
                          }`}
                        >
                          <div
                            className={`p-3 rounded-4 ${
                              msg.sender === "user"
                                ? "bg-primary text-white"
                                : "bg-secondary bg-opacity-25 text-white"
                            }`}
                            style={{
                              maxWidth: "75%",
                              borderBottomRightRadius: msg.sender === "user" ? "4px" : "1rem",
                              borderBottomLeftRadius: msg.sender !== "user" ? "4px" : "1rem",
                            }}
                          >
                            <div className="message-content mb-1">
                              {msg.message}
                            </div>
                            <div className={`small text-white-50`} style={{ fontSize: "0.75rem" }}>
                              {new Date(msg.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>
                  </div>

                  {/* Message Input */}
                  <div className="p-3 border-top">
                    <form onSubmit={handleSendMessage} className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        disabled={selectedTicket.ticketStatus === "closed" || sendingMessage}
                        style={{
                            borderRadius: '20px',
                            padding: '10px 20px',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            background: 'rgba(255, 255, 255, 0.05)',
                            color: 'white'
                        }}
                      />
                      <button
                        type="submit"
                        className={`btn btn-primary px-4 position-relative`}
                        disabled={selectedTicket.ticketStatus === "closed" || sendingMessage || !newMessage.trim()}
                        style={{
                            borderRadius: '20px', 
                            background: '#2563eb',
                            border: 'none',
                            transition: 'all 0.3s ease'
                        }}
                      >
                        {sendingMessage ? (
                          <span className="d-flex align-items-center">
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Sending...
                          </span>
                        ) : (
                          "Send"
                        )}
                      </button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                  <div className="text-center">
                    <MessageSquare size={48} className="mb-3 text-white-50" />
                    <p className="text-white-50">Select a ticket to view messages</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuyerSupport;
